import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ListTabs } from '@/components/ui/list-tabs';
import { ListPagination } from '@/components/ui/list-pagination';
import { ViewToggle } from '@/components/ui/view-toggle';
import { useListParams, usePageClamp } from '@/hooks/use-list-params';
import {
  Video,
  Calendar,
  Clock,
  Play,
  Upload,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Eye,
} from 'lucide-react';
import { format } from 'date-fns';
import { Header, useLayout } from '@/components/layout';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { convertToTitleCase } from '@/lib/utils';
import { SearchInput } from '@/components/ui/search-input';
import { InfoTooltip } from '@/components/ui/tooltip';
import { useDebounce } from '@/hooks/use-debounce';

interface Session {
  id: string;
  claimId: string;
  status: 'SCHEDULED' | 'WAITING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'FAILED';
  scheduledTime?: string;
  startedAt?: string;
  endedAt?: string;
  durationSeconds?: number;
  recordingUrl?: string;
  analysisStatus: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  createdAt: string;
  claim: {
    id: string;
    claimNumber: string;
    claimant: {
      fullName: string;
    };
  };
}

interface VideoUpload {
  id: string;
  claimId: string;
  videoUrl: string;
  filename: string;
  duration?: number;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  createdAt: string;
  claim: {
    id: string;
    claimNumber: string;
    claimant: {
      fullName: string;
    };
  };
}

type VideoSessionItem = { type: 'live'; data: Session } | { type: 'upload'; data: VideoUpload };

export function VideoSessionsPage() {
  const navigate = useNavigate();
  const { isMobile } = useLayout();
  // Tab, search and page live in the URL — see useListParams for the rules.
  const {
    tab,
    setTab,
    search: searchQuery,
    setSearch: setSearchQuery,
    page,
    setPage,
  } = useListParams({ tabs: ['live', 'upload'], tabKey: 'type' });
  const filter: 'all' | 'live' | 'upload' = (tab as 'live' | 'upload' | null) ?? 'all';
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
  const debouncedSearch = useDebounce(searchQuery, 400);
  const limit = 10;

  // Fetch live sessions
  const { data: sessionsRes, isLoading: isLoadingSessions } = useQuery({
    queryKey: ['sessions', 'all', page, debouncedSearch],
    queryFn: async () => {
      const response = await apiClient.get(
        `/video/rooms?page=${page}&limit=${limit}&search=${debouncedSearch}`
      );
      return response.data?.data || response.data;
    },
  });

  // Fetch video uploads
  const { data: uploadsRes, isLoading: isLoadingUploads } = useQuery({
    queryKey: ['video-uploads', 'all', page, debouncedSearch],
    queryFn: async () => {
      const response = await apiClient.get(
        `/video/uploads?page=${page}&limit=${limit}&search=${debouncedSearch}`
      );
      return response.data?.data || response.data;
    },
  });

  const isLoading = isLoadingSessions || isLoadingUploads;

  const sessions = sessionsRes?.data || [];
  const uploads = uploadsRes?.data || [];
  const totalSessions = sessionsRes?.total || 0;
  const totalUploads = uploadsRes?.total || 0;

  // Calculate total pages based on current filter
  const totalPages =
    filter === 'all'
      ? Math.max(sessionsRes?.totalPages || 0, uploadsRes?.totalPages || 0)
      : filter === 'live'
        ? sessionsRes?.totalPages || 0
        : uploadsRes?.totalPages || 0;

  usePageClamp(page, totalPages, setPage);

  // Tab changes drop the page inside useListParams; null is the All view.
  const handleFilterChange = (newFilter: 'all' | 'live' | 'upload') => {
    setTab(newFilter === 'all' ? null : newFilter);
  };

  // Combine and sort sessions
  const allSessions: VideoSessionItem[] = [
    ...(Array.isArray(sessions) ? sessions.map(s => ({ type: 'live' as const, data: s })) : []),
    ...(Array.isArray(uploads) ? uploads.map(u => ({ type: 'upload' as const, data: u })) : []),
  ].sort((a, b) => {
    const dateA = new Date(a.data.createdAt).getTime();
    const dateB = new Date(b.data.createdAt).getTime();
    return dateB - dateA;
  });

  const filteredSessions = allSessions.filter(item => {
    if (filter === 'all') return true;
    return item.type === filter;
  });

  const handleViewSession = (item: VideoSessionItem) => {
    if (item.type === 'live') {
      navigate(`/sessions/${item.data.id}`);
    } else {
      navigate(`/sessions/upload/${item.data.id}`);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<
      string,
      { variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: any }
    > = {
      COMPLETED: { variant: 'default', icon: CheckCircle2 },
      IN_PROGRESS: { variant: 'secondary', icon: Play },
      PROCESSING: { variant: 'secondary', icon: Clock },
      FAILED: { variant: 'destructive', icon: XCircle },
      CANCELLED: { variant: 'outline', icon: XCircle },
      PENDING: { variant: 'secondary', icon: Clock },
      SCHEDULED: { variant: 'outline', icon: Calendar },
      WAITING: { variant: 'outline', icon: Clock },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="text-xs">
        <Icon className="h-3 w-3 mr-1" />
        {convertToTitleCase(status)}
      </Badge>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <Header title="Sessions" description="View and manage all live sessions and video uploads">
        <div className="flex items-center gap-2">
          <SearchInput
            placeholder={isMobile ? 'Search' : 'Search by ID or name...'}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className={isMobile ? 'w-[120px]' : 'w-[280px]'}
          />
        </div>
      </Header>

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Filter Tabs and View Toggle */}
        <ListTabs
          tabs={[
            { value: null, label: 'All', count: totalSessions + totalUploads },
            { value: 'live', label: 'Live Sessions', count: totalSessions },
            { value: 'upload', label: 'Manual Uploads', count: totalUploads },
          ]}
          active={tab}
          onChange={value => handleFilterChange((value as 'live' | 'upload' | null) ?? 'all')}
          end={<ViewToggle value={viewMode} onChange={setViewMode} className="mb-1" />}
        />

        {/* Sessions Grid */}
        <div className="transition-all duration-300">
          {isLoading ? (
            viewMode === 'table' ? (
              <div className="rounded-md border animate-in fade-in duration-300">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead colSpan={7}>
                        <Skeleton className="h-4 w-full" />
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="bg-card">
                    {[...Array(3)].map((_, i) => (
                      <TableRow key={i} className="hover:bg-transparent">
                        <TableCell>
                          <Skeleton className="h-4 w-24" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-32" />
                        </TableCell>
                        <TableCell className="text-center">
                          <Skeleton className="h-4 w-20 rounded-full" />
                        </TableCell>
                        <TableCell className="text-center">
                          <Skeleton className="h-5 w-24 rounded-full" />
                        </TableCell>
                        <TableCell className="text-center">
                          <Skeleton className="h-5 w-24 rounded-full" />
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="space-y-1">
                            <Skeleton className="h-3 w-24" />
                            <Skeleton className="h-3 w-16" />
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Skeleton className="h-8 w-8 ml-auto" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-in fade-in duration-300">
                {[...Array(3)].map((_, i) => (
                  <Card key={i} className="p-4">
                    <Skeleton className="h-40 w-full mb-4" />
                    <Skeleton className="h-4 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2" />
                  </Card>
                ))}
              </div>
            )
          ) : filteredSessions.length === 0 ? (
            <Card className="p-12 text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No sessions found</h3>
              <p className="text-muted-foreground">
                {filter === 'all'
                  ? 'No video sessions or uploads available yet.'
                  : filter === 'live'
                    ? 'No live sessions available yet.'
                    : 'No manual uploads available yet.'}
              </p>
              {/* This list is built from recordings held by the video provider,
                  not from our own session records — a session whose recording
                  the provider does not hold never appears. Saying so stops an
                  empty page reading as "this firm has held no assessments". */}
              <p className="text-xs text-muted-foreground mt-3 max-w-md mx-auto">
                Sessions appear here once the video provider has a recording for
                them. Scheduled and completed assessments are listed on each
                claim and in the schedule.
              </p>
            </Card>
          ) : viewMode === 'table' ? (
            /* Table View */
            <div className="rounded-md border animate-in fade-in duration-300">
              <div
                data-horizontal="true"
                className="bg-card rounded-xl border shadow-sm overflow-x-auto animate-in fade-in duration-300 custom-scrollbar"
              >
                <table className="w-full text-sm text-left">
                  <thead className="bg-muted/50 border-b">
                    <tr className="hover:bg-transparent">
                      <th className="px-6 py-4 font-medium text-muted-foreground">Claim Number</th>
                      <th className="px-6 py-4 font-medium text-muted-foreground">Claimant</th>
                      <th className="px-6 py-4 font-medium text-muted-foreground text-center">
                        Duration
                      </th>
                      <th className="px-6 py-4 font-medium text-muted-foreground text-center">
                        Type
                      </th>
                      <th className="px-6 py-4 font-medium text-muted-foreground text-center">
                        Status
                      </th>
                      <th className="px-6 py-4 font-medium text-muted-foreground text-center">
                        Created
                      </th>
                      <th className="px-6 py-4 font-medium text-muted-foreground text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredSessions.map(item => (
                      <tr
                        key={`${item.type}-${item.data.id}`}
                        className="hover:bg-muted/50 transition-colors"
                      >
                        <td className="px-6 py-4 font-medium">{item.data.claim.claimNumber}</td>
                        <td className="px-6 py-4">{item.data.claim.claimant.fullName}</td>
                        <td className="px-6 py-4 text-center text-xs text-muted-foreground">
                          {item.type === 'live' && item.data.durationSeconds ? (
                            <span>
                              {Math.floor(item.data.durationSeconds / 60)}m{' '}
                              {item.data.durationSeconds % 60}s
                            </span>
                          ) : item.type === 'upload' && item.data.duration ? (
                            <span>
                              {Math.floor(item.data.duration / 60)}m{' '}
                              {Math.floor(item.data.duration % 60)}s
                            </span>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Badge variant="secondary" className="text-xs">
                            {item.type === 'live' ? 'Live Session' : 'Manual Upload'}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {getStatusBadge(item.data.status)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex flex-col text-xs">
                            <span>{format(new Date(item.data.createdAt), 'MMM dd, yyyy')}</span>
                            <span className="text-[11px] text-muted-foreground">
                              {format(new Date(item.data.createdAt), 'hh:mm a')}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <InfoTooltip
                              content="View"
                              direction="top"
                              fontSize="text-[11px]"
                              trigger={
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8"
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleViewSession(item);
                                  }}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              }
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-in fade-in duration-300">
              {filteredSessions.map((item, index) => (
                <Card
                  key={`${item.type}-${item.data.id}`}
                  className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => handleViewSession(item)}
                >
                  {/* Thumbnail */}
                  <div className="relative h-40 bg-muted/30 bg-gradient-to-br from-muted/50 to-muted flex items-center justify-center overflow-hidden">
                    {item.type === 'live' && item.data.recordingUrl ? (
                      <video
                        src={`${item.data.recordingUrl}#t=0.1`}
                        className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity"
                        preload="metadata"
                        muted
                        playsInline
                      />
                    ) : item.type === 'upload' && item.data.videoUrl ? (
                      <video
                        src={`${item.data.videoUrl}#t=0.1`}
                        className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity"
                        preload="metadata"
                        muted
                        playsInline
                      />
                    ) : item.type === 'live' ? (
                      <Video className="h-12 w-12 text-muted-foreground/50" />
                    ) : (
                      <Upload className="h-12 w-12 text-muted-foreground/50" />
                    )}
                    <div className="absolute top-2 right-2">{getStatusBadge(item.data.status)}</div>
                    <div className="absolute top-2 left-2">
                      <Badge variant="secondary" className="text-xs">
                        {item.type === 'live' ? 'Live Session' : 'Manual Upload'}
                      </Badge>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4 space-y-3">
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">
                        {item.data.claim.claimNumber}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {item.data.claim.claimant.fullName}
                      </p>
                    </div>

                    <div className="space-y-2 text-xs text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3 w-3" />
                        <span>{format(new Date(item.data.createdAt), 'MMM dd, yyyy')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-3 w-3" />
                        <span>{format(new Date(item.data.createdAt), 'hh:mm a')}</span>
                      </div>
                      {item.type === 'live' && item.data.durationSeconds && (
                        <div className="flex items-center gap-2">
                          <Video className="h-3 w-3" />
                          <span>
                            Duration: {Math.floor(item.data.durationSeconds / 60)}m{' '}
                            {item.data.durationSeconds % 60}s
                          </span>
                        </div>
                      )}
                      {item.type === 'upload' && item.data.duration && (
                        <div className="flex items-center gap-2">
                          <Video className="h-3 w-3" />
                          <span>
                            Duration: {Math.floor(item.data.duration / 60)}m{' '}
                            {Math.floor(item.data.duration % 60)}s
                          </span>
                        </div>
                      )}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={e => {
                        e.stopPropagation();
                        handleViewSession(item);
                      }}
                    >
                      <Play className="h-4 w-4 mr-2" />
                      View Session
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {!isLoading && (
          <ListPagination page={page} totalPages={totalPages} onPageChange={setPage} />
        )}
      </div>
    </div>
  );
}
